import { useCallback, useMemo } from "react";

import { useWatch } from "antd/es/form/Form";
import { FormInstance } from "antd/es/form/hooks/useForm";
import type { MenuProps } from "antd/es/menu";
import { defineMessages, useIntl } from "react-intl";

import ActivityRecurringUnit from "@mapmycustomers/shared/enum/activity/ActivityRecurringUnit";

import FormValues from "@app/component/createEditEntity/Activity/types/FormValues";
import RecurringIntervalType from "@app/enum/RecurringIntervalType";
import { parseActivityInterval } from "@app/util/activity/activityInterval";
import isAnnualInterval from "@app/util/activity/isAnnualInterval";
import { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";
import { formatDate } from "@app/util/formatters";

import styles from "./useActivityRepeatMenu.module.scss";

export interface UseActivityRepeatMenuOptions {
  form: FormInstance<FormValues>;
  onChange?: (changed: Partial<FormValues>) => void;
}

const messages = defineMessages({
  annually: {
    id: "createActivityModal.footer.scheduleActivity.repeat.annually",
    defaultMessage: "Annually on {date}",
    description: "Annually",
  },
  dateFormatAnnually: {
    id: "createActivityModal.footer.scheduleActivity.repeat.dateFormatAnnually",
    defaultMessage: "MMMM, do",
    description: "Date format for annual recurrence (month and ordinal day)",
  },
  [RecurringIntervalType.CUSTOM]: {
    id: "createActivityModal.footer.scheduleActivity.repeat.custom",
    defaultMessage: "Custom...",
    description: "Custom",
  },
  [RecurringIntervalType.DAILY]: {
    id: "createActivityModal.footer.scheduleActivity.repeat.daily",
    defaultMessage: "Daily (Weekdays Only)",
    description: "Daily (Weekdays Only)",
  },
  [RecurringIntervalType.MONTHLY]: {
    id: "createActivityModal.footer.scheduleActivity.repeat.monthly",
    defaultMessage: "Monthly on {date}",
    description: "Monthly",
  },
  [RecurringIntervalType.NEVER]: {
    id: "createActivityModal.footer.scheduleActivity.repeat.doesNotRepeat",
    defaultMessage: "Does not repeat",
    description: "Does not repeat",
  },
  [RecurringIntervalType.WEEKLY]: {
    id: "createActivityModal.footer.scheduleActivity.repeat.weekly",
    defaultMessage: "Weekly on {date}",
    description: "Weekly",
  },
});

const useActivityRepeatMenu = ({
  form,
  onChange,
}: UseActivityRepeatMenuOptions): MenuProps | undefined => {
  const intl = useIntl();

  const startAt = useWatch(ActivityFieldName.START_AT, form);
  const recurIntervalType = useWatch("recurIntervalType", form);
  const recurInterval = useWatch("recurInterval", form);

  const handleClick = useCallback<NonNullable<MenuProps["onClick"]>>(
    (item) => {
      const intervalType = item.key as RecurringIntervalType;
      const formRecurInterval: FormValues["recurInterval"] = form.getFieldValue("recurInterval");

      let change = {};

      if (item.key === "annually") {
        change = {
          isRecurrent: true,
          recurInterval: {
            unit: ActivityRecurringUnit.MONTH,
            value: 12,
          },
          recurIntervalType: RecurringIntervalType.CUSTOM,
        };
      } else if (intervalType === RecurringIntervalType.CUSTOM) {
        change = {
          isRecurrent: true,
          recurInterval: {
            unit: formRecurInterval?.unit ?? ActivityRecurringUnit.MONTH,
            value: formRecurInterval?.value ?? 1,
          },
          recurIntervalType: intervalType,
        };
      } else if (intervalType === RecurringIntervalType.NEVER) {
        change = {
          isRecurrent: false,
          recurInterval: undefined,
          recurIntervalType: intervalType,
        };
      } else {
        const parsed = parseActivityInterval(intervalType);
        if (parsed) {
          const recurInterval = {
            ...parsed,
            ...(intervalType === RecurringIntervalType.DAILY ? { excludeWeekends: true } : {}),
          };

          change = {
            isRecurrent: true,
            recurInterval,
            recurIntervalType: intervalType,
          };
        }
      }

      form.setFieldsValue(change);

      onChange?.(change);
    },
    [form, onChange]
  );

  return useMemo<MenuProps | undefined>(() => {
    if (!startAt || !Date.parse(startAt.toString())) {
      return undefined;
    }

    const dateAnnually = formatDate(startAt, intl.formatMessage(messages.dateFormatAnnually));
    const dateMonthly = formatDate(startAt, "do");
    const dateWeekly = formatDate(startAt, "EEEE");

    const isAnnual = isAnnualInterval({ recurInterval, recurIntervalType });
    const selectedKeys = isAnnual
      ? ["annually"]
      : recurIntervalType
        ? [recurIntervalType]
        : undefined;

    return {
      className: styles.menu,
      items: [
        {
          key: RecurringIntervalType.CUSTOM,
          label: intl.formatMessage(messages[RecurringIntervalType.CUSTOM]),
        },
        {
          key: "annually",
          label: intl.formatMessage(messages.annually, { date: dateAnnually }),
        },
        {
          key: RecurringIntervalType.MONTHLY,
          label: intl.formatMessage(messages[RecurringIntervalType.MONTHLY], {
            date: dateMonthly,
          }),
        },
        {
          key: RecurringIntervalType.WEEKLY,
          label: intl.formatMessage(messages[RecurringIntervalType.WEEKLY], { date: dateWeekly }),
        },
        {
          key: RecurringIntervalType.DAILY,
          label: intl.formatMessage(messages[RecurringIntervalType.DAILY]),
        },
        {
          key: RecurringIntervalType.NEVER,
          label: intl.formatMessage(messages[RecurringIntervalType.NEVER]),
        },
      ],
      onClick: handleClick,
      selectedKeys,
    };
  }, [intl, startAt, recurInterval, recurIntervalType, handleClick]);
};

export default useActivityRepeatMenu;
